import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  Package,
  FileText,
  User,
  Clock,
  AlertCircle,
  LayoutList,
  LayoutGrid,
  Filter,
  X
} from 'lucide-react';
import { OrderHistoryDto, OrderLineHistoryDto, OrderTextDto } from '../../../../types/orderHistory';
import orderService from '../../../../services/orderService';
import { Card } from '../../../common/Card';
import { BackButton } from '../../../common/BackButton';

interface OrderHistoryProps {
  onBack?: () => void;
}

type ViewMode = 'master-detail' | 'flat';
type ExportFormat = 'excel' | 'csv';

export const OrderHistory: React.FC<OrderHistoryProps> = ({ onBack }) => {
  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('master-detail');
  const [orders, setOrders] = useState<OrderHistoryDto[]>([]);
  const [flatLines, setFlatLines] = useState<OrderLineHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);
  const [showOrderTexts, setShowOrderTexts] = useState(false);

  // Date filtering
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default 7 days ago
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [dateError, setDateError] = useState<string | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Helper function to check if date is valid
  const isValidDate = (date: Date): boolean => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  // Validate date range (max 30 days)
  useEffect(() => {
    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      setDateError('Ugyldig dato');
      return;
    }

    const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 30) {
      setDateError('Maksimalt 30 dagers periode tillatt');
    } else if (daysDiff < 0) {
      setDateError('Fra-dato må være før til-dato');
    } else {
      setDateError(null);
    }
  }, [fromDate, toDate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (dateError || !isValidDate(fromDate) || !isValidDate(toDate)) return;

    setLoading(true);
    setError(null);

    try {
      if (viewMode === 'master-detail') {
        const data = await orderService.getOrdersHistory(fromDate, toDate);
        setOrders(data);
      } else {
        const data = await orderService.getOrderLinesHistoryFlat(fromDate, toDate);
        setFlatLines(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod');
    } finally {
      setLoading(false);
    }
  }, [viewMode, fromDate, toDate, dateError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle order expansion
  const toggleOrder = (orderNo: number) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderNo)) {
      newExpanded.delete(orderNo);
      if (selectedOrder === orderNo) {
        setSelectedOrder(null);
        setShowOrderTexts(false);
      }
    } else {
      newExpanded.add(orderNo);
      setSelectedOrder(orderNo);
      setShowOrderTexts(true);
    }
    setExpandedOrders(newExpanded);
  };

  // Export functionality
  const handleExport = async (format: ExportFormat) => {
    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      setError('Ugyldige datoer valgt');
      return;
    }

    setExporting(true);
    try {
      const blob = await orderService.exportOrderHistory({
        fromDate,
        toDate,
        format,
        view: viewMode
      });

      const filename = `ordrehistorikk_${fromDate.toISOString().split('T')[0]}_${toDate.toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      orderService.downloadFile(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eksport feilet');
    } finally {
      setExporting(false);
    }
  };

  // Filter data
  const filteredOrders = orders.filter(order =>
    order.orderNo.toString().includes(searchTerm) ||
    (order.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.statusText || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFlatLines = flatLines.filter(line =>
    line.orderNo.toString().includes(searchTerm) ||
    (line.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (line.itemNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (line.itemName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected order's texts
  const selectedOrderTexts = selectedOrder
    ? orders.find(o => o.orderNo === selectedOrder)?.orderTexts || []
    : [];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <h1 className="text-2xl font-bold text-gray-900">Ordrehistorikk</h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* View mode toggle */}
            <button
              onClick={() => setViewMode('master-detail')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'master-detail'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutGrid className="inline-block mr-2" size={18} />
              Ordre
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'flat'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutList className="inline-block mr-2" size={18} />
              Ordrelinjer
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fra dato
            </label>
            <input
              type="date"
              value={isValidDate(fromDate) ? fromDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                if (isValidDate(newDate)) {
                  setFromDate(newDate);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Til dato
            </label>
            <input
              type="date"
              value={isValidDate(toDate) ? toDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                if (isValidDate(newDate)) {
                  setToDate(newDate);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Søk
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Søk ordre, kunde..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Export buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Eksport
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => handleExport('excel')}
                disabled={exporting || dateError !== null}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="inline-block mr-1" size={16} />
                Excel
              </button>
              <button
                onClick={() => handleExport('csv')}
                disabled={exporting || dateError !== null}
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="inline-block mr-1" size={16} />
                CSV
              </button>
            </div>
          </div>
        </div>

        {/* Date error */}
        {dateError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-800">
            <AlertCircle className="mr-2 flex-shrink-0" size={20} />
            <span className="text-sm">{dateError}</span>
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto p-6">
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Laster ordrehistorikk...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-800">
            <AlertCircle className="mr-3 flex-shrink-0" size={24} />
            <div>
              <p className="font-medium">Feil ved lasting</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && viewMode === 'master-detail' && (
          <>
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Package className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600">Ingen ordre funnet for valgt periode</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((order) => (
                  <Card key={order.orderNo} className="overflow-hidden">
                    {/* Order header */}
                    <button
                      onClick={() => toggleOrder(order.orderNo)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        {expandedOrders.has(order.orderNo) ? (
                          <ChevronDown size={20} className="text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-500 flex-shrink-0" />
                        )}
                        <div className="text-left">
                          <div className="flex items-center space-x-3">
                            <span className="font-bold text-lg">Ordre {order.orderNo}</span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {order.statusText}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {order.customerName} • {new Date(order.orderDate).toLocaleDateString('no-NO')}
                            {order.deliveryDate && ` • Levering: ${new Date(order.deliveryDate).toLocaleDateString('no-NO')}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6 text-sm">
                        <div className="text-right">
                          <div className="text-gray-500">Linjer</div>
                          <div className="font-medium">{order.totalLines}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-500">Antall</div>
                          <div className="font-medium">{order.totalQty}</div>
                        </div>
                        {order.pListID && (
                          <div className="text-right">
                            <div className="text-gray-500">Plukkliste</div>
                            <div className="font-medium">#{order.pListID}</div>
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Order lines */}
                    {expandedOrders.has(order.orderNo) && order.lines && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-100 border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Linjenr</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Varenr</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Varenavn</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Bestilt</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Plukket</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Levert</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Enhet</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Lokasjon</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Operatør</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.lines.map((line) => (
                                <tr key={line.orderLineNo} className="border-b border-gray-200 hover:bg-white">
                                  <td className="px-4 py-2 text-sm">{line.orderLineNo}</td>
                                  <td className="px-4 py-2 text-sm font-medium">{line.itemNo}</td>
                                  <td className="px-4 py-2 text-sm">{line.itemName}</td>
                                  <td className="px-4 py-2 text-sm text-right">{line.qtyOrdered}</td>
                                  <td className="px-4 py-2 text-sm text-right font-medium">{line.qtyPicked}</td>
                                  <td className="px-4 py-2 text-sm text-right">{line.qtyDelivered}</td>
                                  <td className="px-4 py-2 text-sm">{line.unit || '-'}</td>
                                  <td className="px-4 py-2 text-sm">{line.locationNo || '-'}</td>
                                  <td className="px-4 py-2 text-sm">{line.operatorName || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && !error && viewMode === 'flat' && (
          <>
            {filteredFlatLines.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <FileText className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600">Ingen ordrelinjer funnet for valgt periode</p>
              </div>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Ordrenr</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Linjenr</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Kunde</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Varenr</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Varenavn</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Bestilt</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Plukket</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Levert</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Plukkliste</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Lokasjon</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Operatør</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFlatLines.map((line, idx) => (
                        <tr key={`${line.orderNo}-${line.orderLineNo}`} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium">{line.orderNo}</td>
                          <td className="px-4 py-2 text-sm">{line.orderLineNo}</td>
                          <td className="px-4 py-2 text-sm">{line.customerName}</td>
                          <td className="px-4 py-2 text-sm font-medium">{line.itemNo}</td>
                          <td className="px-4 py-2 text-sm">{line.itemName}</td>
                          <td className="px-4 py-2 text-sm text-right">{line.qtyOrdered}</td>
                          <td className="px-4 py-2 text-sm text-right font-medium">{line.qtyPicked}</td>
                          <td className="px-4 py-2 text-sm text-right">{line.qtyDelivered}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {line.statusText}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">{line.pListID || '-'}</td>
                          <td className="px-4 py-2 text-sm">{line.locationNo || '-'}</td>
                          <td className="px-4 py-2 text-sm">{line.operatorName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {/* OrdersTxt Panel - Only shown when order is selected and has texts */}
      {showOrderTexts && selectedOrderTexts.length > 0 && (
        <div className="border-t border-gray-200 bg-white">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <FileText className="mr-2" size={18} />
                Ordretekster (Ordre {selectedOrder})
              </h3>
              <button
                onClick={() => {
                  setShowOrderTexts(false);
                  setSelectedOrder(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
              {selectedOrderTexts.map((text, idx) => (
                <div key={`${text.textNo}-${text.textLine}`} className="text-sm text-gray-700 mb-1">
                  <span className="font-medium text-gray-500 mr-2">
                    [{text.textNo}.{text.textLine}]
                  </span>
                  {text.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
